import { Link, useParams } from "@tanstack/solid-router";
import { Show, createSignal, createEffect } from "solid-js";

import { AccountContactsCard } from "~/components/AccountContactsCard";
import { AccountPicker } from "~/components/AccountPicker";
import { AccountSubsidiariesCard } from "~/components/AccountSubsidiariesCard";
import { AppShell } from "~/components/AppShell";
import { recordView } from "~/lib/recentViews";
import { MergeAccountDialog } from "~/components/MergeAccountDialog";
import { CustomFieldsRenderer } from "~/components/CustomFieldsRenderer";
import { DealsForEntityCard } from "~/components/DealsForEntityCard";
import { EntityTimeline } from "~/components/EntityTimeline";
import { LifecycleProgress } from "~/components/LifecycleProgress";
import { TagBadges, TagsEditor } from "~/components/TagsEditor";
import { UserPicker } from "~/components/UserPicker";
import { AccountDraftEmailDialog } from "~/components/AccountDraftEmailDialog";
import { BriefModal } from "~/components/BriefModal";
import {
  useAccount,
  useGenerateAccountBrief,
  useUpdateAccount,
  useWorkspaceUser,
} from "~/lib/hooks";
import type { BriefResponse } from "~/lib/types";

export function AccountDetailPage() {
  const params = useParams({ strict: false }) as () => { wsKey: string; accId: string };

  const account = useAccount(
    () => params().wsKey,
    () => params().accId,
  );
  const updateMutation = useUpdateAccount(() => params().wsKey);

  const [editing, setEditing] = createSignal(false);
  const [showMerge, setShowMerge] = createSignal(false);
  const [showDraftEmail, setShowDraftEmail] = createSignal(false);
  const briefMutation = useGenerateAccountBrief(() => params().wsKey);
  const [showBrief, setShowBrief] = createSignal(false);
  const [briefError, setBriefError] = createSignal<string | null>(null);
  const [briefResult, setBriefResult] = createSignal<BriefResponse | null>(null);
  const runBrief = async () => {
    setShowBrief(true);
    setBriefError(null);
    setBriefResult(null);
    try {
      const r = await briefMutation.mutateAsync({ accId: params().accId });
      setBriefResult(r);
    } catch (err) {
      setBriefError((err as Error).message);
    }
  };
  const [formError, setFormError] = createSignal<string | null>(null);
  const [customDraft, setCustomDraft] = createSignal<Record<string, unknown>>({});
  const [tagsDraft, setTagsDraft] = createSignal<string[]>([]);
  const [ownerDraft, setOwnerDraft] = createSignal<string | null>(null);
  const [ownerNameDraft, setOwnerNameDraft] = createSignal<string | null>(null);
  const [parentDraft, setParentDraft] = createSignal<string | null>(null);
  const [parentNameDraft, setParentNameDraft] = createSignal<string | null>(null);

  // Hydrate parent's display name for the view + edit chip.
  const parent = useAccount(
    () => params().wsKey,
    () => account.data?.parent_account_id ?? "",
  );

  // Hydrate the owner's display name for view mode (the account itself only
  // carries the raw owner_user_id).
  const owner = useWorkspaceUser(
    () => params().wsKey,
    () => account.data?.owner_user_id ?? null,
  );

  createEffect(() => {
    if (account.data) setFormError(null);
  });

  // Recently viewed tracking — fires when the loaded account changes.
  createEffect(() => {
    const a = account.data;
    if (!a) return;
    recordView(params().wsKey, {
      entity_type: "account",
      id: a.id,
      name: a.name,
      subtitle: a.primary_domain ?? a.lifecycle_stage,
    });
  });

  // Seed drafts (custom fields + tags) from the persisted state when the
  // form opens, so cancelling discards the in-progress edit.
  createEffect(() => {
    if (editing() && account.data) {
      setCustomDraft({ ...(account.data.custom_fields ?? {}) });
      setTagsDraft([...(account.data.tags ?? [])]);
      setOwnerDraft(account.data.owner_user_id ?? null);
      setOwnerNameDraft(owner.data ? (owner.data.display_name ?? owner.data.email) : null);
      setParentDraft(account.data.parent_account_id ?? null);
      setParentNameDraft(parent.data?.name ?? null);
    }
  });

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    setFormError(null);
    const form = e.target as HTMLFormElement;
    const data = new FormData(form);
    if (!account.data) return;
    try {
      const body: Record<string, unknown> = {
        name: String(data.get("name") ?? account.data.name),
        legal_name: (String(data.get("legal_name") ?? "") || null) as string | null,
        primary_domain: (String(data.get("primary_domain") ?? "") || null) as string | null,
        primary_email: (String(data.get("primary_email") ?? "") || null) as string | null,
        primary_phone: (String(data.get("primary_phone") ?? "") || null) as string | null,
        website: (String(data.get("website") ?? "") || null) as string | null,
        industry: (String(data.get("industry") ?? "") || null) as string | null,
        description: (String(data.get("description") ?? "") || null) as string | null,
        lifecycle_stage: String(data.get("lifecycle_stage") ?? account.data.lifecycle_stage),
        kind: String(data.get("kind") ?? account.data.kind),
        custom_fields: customDraft(),
        tags: tagsDraft(),
      };
      if (ownerDraft() !== (account.data.owner_user_id ?? null)) {
        body.owner_user_id = ownerDraft();
      }
      if (parentDraft() !== (account.data.parent_account_id ?? null)) {
        body.parent_account_id = parentDraft();
      }
      await updateMutation.mutateAsync({
        accId: account.data.id,
        version: account.data.version,
        body,
      });
      setEditing(false);
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === "OptimisticLockError") {
        setFormError(
          "This account was changed by someone else. Reload to see the latest, then re-apply your edits.",
        );
      } else {
        setFormError(e.message);
      }
    }
  };

  return (
    <AppShell
      wsKey={params().wsKey}
      pageTitle="Account"
      rightSlot={
        <Link
          to="/workspaces/$wsKey/accounts"
          params={{ wsKey: params().wsKey }}
          class="btn btn-ghost btn-sm"
        >
          ← Back to list
        </Link>
      }
    >
      <Show when={account.isLoading}>
        <div role="status" aria-live="polite" class="text-center py-8 opacity-60">
          Loading…
        </div>
      </Show>
      <Show when={account.isError}>
        <div role="alert" class="alert alert-error">
          Failed to load account.
        </div>
      </Show>
      <Show when={account.data}>
        {(data) => (
          <div class="card bg-base-100 shadow-sm">
            <div class="card-body">
              <div class="flex items-start justify-between">
                <div>
                  <h2 class="card-title">{data().name}</h2>
                  <div class="flex gap-2 mt-1">
                    <span class="badge badge-ghost badge-sm">{data().kind}</span>
                    <span class="badge badge-outline badge-sm">{data().lifecycle_stage}</span>
                    <Show when={data().scaicontrol_customer_id}>
                      <span
                        class="badge badge-primary badge-sm"
                        title="This Account is a ScaiControl customer"
                      >
                        ScaiControl
                      </span>
                    </Show>
                  </div>
                </div>
                <Show when={!editing()}>
                  <div class="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={runBrief}
                      disabled={briefMutation.isPending}
                      aria-label="Generate an AI brief about this account"
                    >
                      {briefMutation.isPending ? "Generating…" : "✨ Generate brief"}
                    </button>
                    <button
                      type="button"
                      class="btn btn-ghost btn-sm"
                      onClick={() => setShowDraftEmail(true)}
                      aria-label="Draft an email on behalf of this account"
                    >
                      ✉️ Draft email
                    </button>
                    <button class="btn btn-ghost btn-sm" onClick={() => setShowMerge(true)}>
                      Merge…
                    </button>
                    <button class="btn btn-primary btn-sm" onClick={() => setEditing(true)}>
                      Edit
                    </button>
                  </div>
                </Show>
              </div>

              <Show when={!editing()}>
                <div class="mt-4">
                  <LifecycleProgress
                    stages={["prospect", "qualified", "customer", "churned"]}
                    terminalStages={["churned"]}
                    current={data().lifecycle_stage}
                    onSelect={async (stage) => {
                      if (stage === data().lifecycle_stage) return;
                      await updateMutation.mutateAsync({
                        accId: data().id,
                        version: data().version,
                        body: { lifecycle_stage: stage },
                      });
                    }}
                  />
                </div>
                <dl class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 mt-4">
                  <DetailRow label="Legal name" value={data().legal_name} />
                  <DetailRow label="Primary domain" value={data().primary_domain} />
                  <DetailRow label="Primary email" value={data().primary_email} />
                  <DetailRow label="Primary phone" value={data().primary_phone} />
                  <DetailRow label="Website" value={data().website} />
                  <DetailRow label="Industry" value={data().industry} />
                  <DetailRow
                    label="Owner"
                    value={
                      owner.data
                        ? (owner.data.display_name ?? owner.data.email)
                        : data().owner_user_id
                          ? "—"
                          : null
                    }
                  />
                  <DetailRow
                    label="Parent account"
                    value={parent.data ? parent.data.name : data().parent_account_id ? "—" : null}
                    linkTo={
                      data().parent_account_id
                        ? {
                            to: "/workspaces/$wsKey/accounts/$accId",
                            params: {
                              wsKey: params().wsKey,
                              accId: data().parent_account_id!,
                            },
                          }
                        : null
                    }
                  />
                  <DetailRow label="Created" value={new Date(data().created_at).toLocaleString()} />
                  <DetailRow label="Updated" value={new Date(data().updated_at).toLocaleString()} />
                  <DetailRow label="Version" value={String(data().version)} />
                </dl>
                <Show when={data().description}>
                  <div class="mt-4">
                    <div class="text-xs uppercase opacity-50 mb-1">Description</div>
                    <p class="whitespace-pre-wrap">{data().description}</p>
                  </div>
                </Show>
                <Show when={(data().tags?.length ?? 0) > 0}>
                  <div class="mt-4">
                    <div class="text-xs uppercase opacity-50 mb-1">Tags</div>
                    <TagBadges tags={() => data().tags} />
                  </div>
                </Show>
                <div class="mt-4">
                  <CustomFieldsRenderer
                    wsKey={() => params().wsKey}
                    entityType="account"
                    values={() => data().custom_fields}
                    onChange={() => {
                      /* read-only in view mode */
                    }}
                    variant="view"
                  />
                </div>
              </Show>

              <Show when={editing()}>
                <form onSubmit={handleSubmit} class="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                  <LabeledInput name="name" label="Name" value={data().name} required />
                  <LabeledInput
                    name="legal_name"
                    label="Legal name"
                    value={data().legal_name ?? ""}
                  />
                  <LabeledInput
                    name="primary_domain"
                    label="Primary domain"
                    value={data().primary_domain ?? ""}
                  />
                  <LabeledInput
                    name="primary_email"
                    label="Primary email"
                    type="email"
                    value={data().primary_email ?? ""}
                  />
                  <LabeledInput
                    name="primary_phone"
                    label="Primary phone"
                    value={data().primary_phone ?? ""}
                  />
                  <LabeledInput name="website" label="Website" value={data().website ?? ""} />
                  <LabeledInput name="industry" label="Industry" value={data().industry ?? ""} />
                  <LabeledSelect
                    name="lifecycle_stage"
                    label="Lifecycle stage"
                    value={data().lifecycle_stage}
                    options={["prospect", "qualified", "customer", "churned"]}
                  />
                  <LabeledSelect
                    name="kind"
                    label="Kind"
                    value={data().kind}
                    options={[
                      "company",
                      "household",
                      "government",
                      "nonprofit",
                      "partner",
                      "other",
                    ]}
                  />
                  <div class="md:col-span-2">
                    <label for="account-description" class="label">
                      <span class="label-text">Description</span>
                    </label>
                    <textarea
                      id="account-description"
                      name="description"
                      class="textarea textarea-bordered w-full"
                      rows="3"
                    >
                      {data().description ?? ""}
                    </textarea>
                  </div>
                  <div class="md:col-span-2">
                    <label class="label">
                      <span class="label-text">Tags</span>
                    </label>
                    <TagsEditor
                      wsKey={() => params().wsKey}
                      entityType="account"
                      value={tagsDraft}
                      onChange={setTagsDraft}
                    />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Owner</span>
                    </label>
                    <UserPicker
                      wsKey={() => params().wsKey}
                      value={ownerDraft}
                      selectedName={ownerNameDraft}
                      onChange={(id, name) => {
                        setOwnerDraft(id);
                        setOwnerNameDraft(name);
                      }}
                    />
                  </div>
                  <div>
                    <label class="label">
                      <span class="label-text">Parent account</span>
                    </label>
                    <AccountPicker
                      wsKey={() => params().wsKey}
                      value={parentDraft}
                      selectedName={parentNameDraft}
                      onChange={(id, name) => {
                        // Self-reference guard: an account cannot be its own
                        // parent. The full ancestor-cycle check lives on the
                        // backend; this just protects the obvious mistake.
                        if (id && id === account.data?.id) {
                          setFormError("An account cannot be its own parent.");
                          return;
                        }
                        setFormError(null);
                        setParentDraft(id);
                        setParentNameDraft(name);
                      }}
                    />
                  </div>
                  <div class="md:col-span-2">
                    <CustomFieldsRenderer
                      wsKey={() => params().wsKey}
                      entityType="account"
                      values={() => customDraft()}
                      onChange={(key, value) =>
                        setCustomDraft((prev) => ({ ...prev, [key]: value }))
                      }
                    />
                  </div>
                  <Show when={formError()}>
                    <div class="md:col-span-2 alert alert-error text-sm">{formError()}</div>
                  </Show>
                  <div class="md:col-span-2 flex gap-2 justify-end">
                    <button type="button" class="btn btn-ghost" onClick={() => setEditing(false)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      class="btn btn-primary"
                      disabled={updateMutation.isPending}
                    >
                      {updateMutation.isPending ? "Saving…" : "Save"}
                    </button>
                  </div>
                </form>
              </Show>
            </div>
          </div>
        )}
      </Show>

      <Show when={account.data}>
        {(data) => (
          <AccountSubsidiariesCard wsKey={() => params().wsKey} accountId={() => data().id} />
        )}
      </Show>

      <Show when={account.data}>
        {(data) => <AccountContactsCard wsKey={() => params().wsKey} accountId={() => data().id} />}
      </Show>

      <Show when={account.data}>
        {(data) => (
          <DealsForEntityCard
            wsKey={() => params().wsKey}
            entityType="account"
            entityId={() => data().id}
          />
        )}
      </Show>

      <Show when={account.data}>
        {(data) => (
          <EntityTimeline
            wsId={() => params().wsKey}
            entityType="account"
            entityId={() => data().id}
          />
        )}
      </Show>

      <Show when={showMerge()}>
        <Show when={account.data}>
          {(data) => (
            <MergeAccountDialog
              wsKey={() => params().wsKey}
              doomed={data()}
              onClose={() => setShowMerge(false)}
            />
          )}
        </Show>
      </Show>

      <Show when={showDraftEmail()}>
        <AccountDraftEmailDialog
          wsKey={() => params().wsKey}
          accId={() => params().accId}
          accountName={() => account.data?.name ?? "this account"}
          onClose={() => setShowDraftEmail(false)}
        />
      </Show>

      <Show when={showBrief()}>
        <BriefModal
          subjectLabel={account.data?.name ?? "this account"}
          pending={briefMutation.isPending}
          error={briefError()}
          result={briefResult()}
          onClose={() => setShowBrief(false)}
          onRegenerate={runBrief}
        />
      </Show>
    </AppShell>
  );
}

function DetailRow(props: {
  label: string;
  value: string | null;
  linkTo?: { to: string; params: Record<string, string> } | null;
}) {
  return (
    <div>
      <dt class="text-xs uppercase opacity-50">{props.label}</dt>
      <dd class="font-medium">
        <Show when={props.value} fallback={<>—</>}>
          <Show when={props.linkTo} fallback={<span>{props.value}</span>}>
            {(linkProps) => (
              <Link to={linkProps().to} params={linkProps().params} class="link link-primary">
                {props.value}
              </Link>
            )}
          </Show>
        </Show>
      </dd>
    </div>
  );
}

function LabeledInput(props: {
  name: string;
  label: string;
  value: string;
  type?: string;
  required?: boolean;
}) {
  const id = `account-field-${props.name}`;
  return (
    <div>
      <label for={id} class="label">
        <span class="label-text">{props.label}</span>
      </label>
      <input
        id={id}
        name={props.name}
        class="input input-bordered w-full"
        value={props.value}
        type={props.type ?? "text"}
        required={props.required}
      />
    </div>
  );
}

function LabeledSelect(props: {
  name: string;
  label: string;
  value: string;
  options: string[];
}) {
  const id = `account-field-${props.name}`;
  return (
    <div>
      <label for={id} class="label">
        <span class="label-text">{props.label}</span>
      </label>
      <select id={id} name={props.name} class="select select-bordered w-full">
        {props.options.map((o) => (
          <option value={o} selected={o === props.value}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
